/**
 * Shared formatters for the Business app.
 *
 * All user-facing numbers and dates MUST go through these helpers so we get
 * consistent locale handling (TR/EN) and currency rendering.
 */
import i18next from 'i18next';

function getLocale(): string {
  return i18next.resolvedLanguage ?? i18next.language ?? 'tr';
}

export function formatMoney(
  amount: number | null | undefined,
  currency: string | null | undefined = 'TRY',
  options: Intl.NumberFormatOptions = {},
): string {
  if (amount === null || amount === undefined || Number.isNaN(amount)) {
    return '—';
  }
  try {
    return new Intl.NumberFormat(getLocale(), {
      style: 'currency',
      currency: currency ?? 'TRY',
      maximumFractionDigits: 0,
      ...options,
    }).format(amount);
  } catch {
    return `${amount} ${currency ?? 'TRY'}`;
  }
}

export function formatDate(
  iso: string | null | undefined,
  options: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'short', year: 'numeric' },
): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return new Intl.DateTimeFormat(getLocale(), options).format(d);
}

export function formatDateTime(iso: string | null | undefined): string {
  return formatDate(iso, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function formatRelative(iso: string | null | undefined): string {
  if (!iso) return '—';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '—';
  const diff = Date.now() - then;
  const abs = Math.abs(diff);
  const minute = 60_000;
  const hour = 60 * minute;
  const day = 24 * hour;
  const week = 7 * day;
  const month = 30 * day;
  const year = 365 * day;

  const rtf = new Intl.RelativeTimeFormat(getLocale(), { numeric: 'auto' });
  const sign = diff >= 0 ? -1 : 1;
  if (abs < minute) return rtf.format(0, 'second');
  if (abs < hour) return rtf.format(sign * Math.round(abs / minute), 'minute');
  if (abs < day) return rtf.format(sign * Math.round(abs / hour), 'hour');
  if (abs < week) return rtf.format(sign * Math.round(abs / day), 'day');
  if (abs < month) return rtf.format(sign * Math.round(abs / week), 'week');
  if (abs < year) return rtf.format(sign * Math.round(abs / month), 'month');
  return rtf.format(sign * Math.round(abs / year), 'year');
}

/** Computes age in whole years from a birth-date ISO string. */
export function computeAgeYears(iso: string | null | undefined): number | null {
  if (!iso) return null;
  const birth = new Date(iso);
  if (Number.isNaN(birth.getTime())) return null;
  const now = new Date();
  let years = now.getFullYear() - birth.getFullYear();
  const monthDiff = now.getMonth() - birth.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && now.getDate() < birth.getDate())) {
    years -= 1;
  }
  return years;
}
